import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Video as VideoIcon,
  Image as ImageIcon,
  Upload,
  Loader2,
  Star,
  Eye,
  EyeOff,
  Play,
  Pencil,
  Download,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
};

const AdminGalleryPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const [activeTab, setActiveTab] = useState("photos");

  useEffect(() => {
    if (!token) navigate("/espace-pro-fastandcar");
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-black text-white pt-32 md:pt-40">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/espace-pro-fastandcar/dashboard")}
              data-testid="gallery-back-btn"
              className="text-white hover:bg-zinc-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="font-unbounded text-3xl font-black">Galerie</h1>
              <p className="text-zinc-400 text-sm mt-1">
                Gérez les photos et vidéos de la page <span className="text-neon-lime">Galerie publique</span>
              </p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
            <TabsTrigger
              value="photos"
              data-testid="tab-photos"
              className="data-[state=active]:bg-neon-lime data-[state=active]:text-black font-bold"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Photos
            </TabsTrigger>
            <TabsTrigger
              value="videos"
              data-testid="tab-videos"
              className="data-[state=active]:bg-neon-lime data-[state=active]:text-black font-bold"
            >
              <VideoIcon className="w-4 h-4 mr-2" />
              Vidéos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="photos">
            <GalleryPhotosPanel token={token} />
          </TabsContent>
          <TabsContent value="videos">
            <GalleryVideosPanel token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ============ PHOTOS PANEL ============

export const GalleryPhotosPanel = ({ token }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileInputRef = useRef(null);

  const empty = {
    brand: "",
    model: "",
    year: new Date().getFullYear(),
    origin: "Allemagne",
    description: "",
    image_url: "",
    is_visible: true,
    file: null,
  };
  const [formData, setFormData] = useState(empty);

  const fetchPhotos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/gallery-photos?token=${token}`);
      setPhotos(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement des photos");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const openAdd = () => { setEditing(null); setFormData(empty); setIsDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setFormData({ ...p, file: null });
    setIsDialogOpen(true);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/avif"];
    if (!allowed.includes(file.type)) {
      toast.error("Format non supporté (JPEG, PNG, WebP, AVIF)");
      return;
    }
    setFormData((fd) => ({ ...fd, file }));
  };

  const uploadImage = async (file) => {
    const data = new FormData();
    data.append("file", file);
    const res = await axios.post(`${API}/upload-image?token=${token}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
      },
      timeout: 600000,
    });
    return res.data.url;
  };

  const handleSave = async () => {
    if (!formData.brand.trim() || !formData.model.trim()) {
      toast.error("Marque et modèle obligatoires");
      return;
    }
    try {
      setUploading(true);
      setUploadProgress(0);
      let image_url = formData.image_url;
      if (formData.file) {
        image_url = await uploadImage(formData.file);
      }
      if (!image_url) {
        toast.error("Sélectionnez une image");
        setUploading(false);
        return;
      }
      const payload = {
        brand: formData.brand.trim(),
        model: formData.model.trim(),
        year: parseInt(formData.year) || new Date().getFullYear(),
        origin: formData.origin.trim() || "Allemagne",
        description: formData.description || "",
        image_url,
        is_visible: formData.is_visible,
      };
      if (editing) {
        await axios.put(`${API}/gallery-photos/${editing.id}?token=${token}`, payload);
        toast.success("Photo mise à jour");
      } else {
        await axios.post(`${API}/gallery-photos?token=${token}`, payload);
        toast.success("Photo ajoutée");
      }
      setIsDialogOpen(false);
      setFormData(empty);
      fetchPhotos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await axios.delete(`${API}/gallery-photos/${id}?token=${token}`);
      toast.success("Photo supprimée");
      fetchPhotos();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleVisibility = async (p) => {
    try {
      await axios.put(`${API}/gallery-photos/${p.id}?token=${token}`, {
        is_visible: !p.is_visible,
      });
      fetchPhotos();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-zinc-400 text-sm">
          {photos.length} photo{photos.length > 1 ? "s" : ""} dans la galerie publique
        </p>
        <Button
          onClick={openAdd}
          data-testid="add-photo-btn"
          className="bg-neon-lime text-black font-bold hover:bg-neon-lime/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle photo
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-neon-lime" />
        </div>
      ) : photos.length === 0 ? (
        <div className="border border-zinc-800 p-16 text-center">
          <ImageIcon className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-400 mb-4">Aucune photo pour le moment</p>
          <Button onClick={openAdd} className="bg-neon-lime text-black font-bold">
            <Upload className="w-4 h-4 mr-2" />
            Ajouter ma première photo
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => (
            <div
              key={p.id}
              data-testid={`photo-card-${p.id}`}
              className="border border-zinc-800 bg-zinc-900/50 overflow-hidden hover:border-neon-lime/50 transition-colors"
            >
              <div className="relative aspect-[4/3] bg-black">
                <img src={p.image_url} alt={`${p.brand} ${p.model}`} className="w-full h-full object-cover" />
                {!p.is_visible && (
                  <span className="absolute top-2 left-2 bg-zinc-700 text-white text-xs font-bold px-2 py-1">
                    Masquée
                  </span>
                )}
              </div>
              <div className="p-3">
                <h3 className="font-bold text-white text-sm truncate" title={`${p.brand} ${p.model}`}>
                  {p.brand} {p.model}
                </h3>
                <p className="text-zinc-500 text-xs mt-0.5">
                  {p.year} • {p.origin}
                </p>
                <div className="flex gap-1.5 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleVisibility(p)}
                    data-testid={`photo-toggle-${p.id}`}
                    className="border-zinc-700 text-white hover:bg-zinc-800 flex-1 h-8 px-2"
                  >
                    {p.is_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </Button>
                  <a
                    href={p.image_url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`photo-download-${p.id}`}
                    className="inline-flex items-center justify-center h-8 px-2 border border-zinc-700 text-white hover:bg-zinc-800 flex-1 rounded-md text-sm"
                    title="Télécharger"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(p)}
                    data-testid={`photo-edit-${p.id}`}
                    className="border-zinc-700 text-white hover:bg-zinc-800 flex-1 h-8 px-2"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(p.id)}
                    data-testid={`photo-delete-${p.id}`}
                    className="border-red-900 text-red-400 hover:bg-red-950 flex-1 h-8 px-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => { if (!uploading) { setIsDialogOpen(o); if (!o) setFormData(empty); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-unbounded">
              {editing ? "Modifier la photo" : "Nouvelle photo"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white">Image</Label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-zinc-700 hover:border-neon-lime/50 p-6 text-center cursor-pointer mt-1"
                data-testid="photo-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/avif"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
                {formData.file ? (
                  <div>
                    <ImageIcon className="w-10 h-10 mx-auto text-neon-lime mb-2" />
                    <p className="text-white font-bold truncate">{formData.file.name}</p>
                    <p className="text-zinc-500 text-xs mt-1">{formatSize(formData.file.size)} — Cliquez pour changer</p>
                  </div>
                ) : formData.image_url ? (
                  <div>
                    <img src={formData.image_url} alt="" className="max-h-32 mx-auto mb-2 object-contain" />
                    <p className="text-zinc-500 text-xs">Cliquez pour remplacer l'image</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 mx-auto text-zinc-500 mb-2" />
                    <p className="text-white font-bold">Sélectionnez ou glissez une image</p>
                    <p className="text-zinc-500 text-xs mt-1">JPEG, PNG, WebP — aucune limite de taille</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Marque *</Label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  placeholder="Audi"
                  data-testid="photo-brand"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Modèle *</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="SQ5 3.0 TDI"
                  data-testid="photo-model"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Année</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  data-testid="photo-year"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Origine</Label>
                <Input
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  placeholder="Allemagne"
                  data-testid="photo-origin"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-white">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="326cv, import Berlin"
                rows={2}
                data-testid="photo-description"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>

            <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
              <Label className="text-white">Visible sur le site</Label>
              <Switch
                checked={formData.is_visible}
                onCheckedChange={(c) => setFormData({ ...formData, is_visible: c })}
              />
            </div>

            {uploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white">Upload en cours…</span>
                  <span className="text-neon-lime font-bold">{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-zinc-800 overflow-hidden">
                  <div className="h-full bg-neon-lime transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={uploading}
                className="border-zinc-700 text-white hover:bg-zinc-800 flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSave}
                disabled={uploading}
                data-testid="save-photo-btn"
                className="bg-neon-lime text-black font-bold hover:bg-neon-lime/90 flex-1"
              >
                {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Upload…</>) : editing ? "Enregistrer" : "Ajouter"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ VIDEOS PANEL ============

export const GalleryVideosPanel = ({ token }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const fileInputRef = useRef(null);

  const empty = {
    title: "",
    description: "",
    is_presentation: false,
    is_visible: true,
    file: null,
  };
  const [formData, setFormData] = useState(empty);

  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/gallery-videos?token=${token}`);
      setVideos(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement des vidéos");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const openAdd = () => { setEditing(null); setFormData(empty); setIsDialogOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setFormData({
      title: v.title,
      description: v.description || "",
      is_presentation: v.is_presentation,
      is_visible: v.is_visible,
      file: null,
    });
    setIsDialogOpen(true);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const allowed = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v", "video/mpeg"];
    if (!allowed.includes(file.type)) {
      toast.error("Format non supporté (MP4, MOV, WebM)");
      return;
    }
    setFormData((fd) => ({ ...fd, file, title: fd.title || file.name.replace(/\.[^.]+$/, "") }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  const handleSave = async () => {
    if (editing) {
      try {
        await axios.put(`${API}/gallery-videos/${editing.id}?token=${token}`, {
          title: formData.title,
          description: formData.description,
          is_presentation: formData.is_presentation,
          is_visible: formData.is_visible,
        });
        toast.success("Vidéo mise à jour");
        setIsDialogOpen(false);
        setFormData(empty);
        fetchVideos();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur de mise à jour");
      }
      return;
    }

    if (!formData.file) { toast.error("Sélectionnez une vidéo"); return; }
    if (!formData.title.trim()) { toast.error("Le titre est obligatoire"); return; }

    try {
      setUploading(true);
      setUploadProgress(0);
      const data = new FormData();
      data.append("file", formData.file);
      const params = new URLSearchParams({
        title: formData.title.trim(),
        is_presentation: formData.is_presentation,
        token,
      });
      if (formData.description) params.append("description", formData.description);
      await axios.post(`${API}/upload-video?${params.toString()}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
        timeout: 1800000, // 30 min for very large videos
      });
      toast.success("Vidéo uploadée !");
      setIsDialogOpen(false);
      setFormData(empty);
      fetchVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement cette vidéo ?")) return;
    try {
      await axios.delete(`${API}/gallery-videos/${id}?token=${token}`);
      toast.success("Vidéo supprimée");
      fetchVideos();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleVisibility = async (v) => {
    try {
      await axios.put(`${API}/gallery-videos/${v.id}?token=${token}`, { is_visible: !v.is_visible });
      fetchVideos();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleDownload = async (v) => {
    try {
      setDownloadingId(v.id);
      const res = await axios.get(`${API}/videos/${v.id}/download?token=${token}`, {
        responseType: "blob",
        timeout: 1800000,
      });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${v.title || "video"}.mp4`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || v.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-zinc-400 text-sm">
          {videos.length} vidéo{videos.length > 1 ? "s" : ""} dans la galerie publique
        </p>
        <Button
          onClick={openAdd}
          data-testid="add-video-btn"
          className="bg-neon-lime text-black font-bold hover:bg-neon-lime/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle vidéo
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-neon-lime" />
        </div>
      ) : videos.length === 0 ? (
        <div className="border border-zinc-800 p-16 text-center">
          <VideoIcon className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-400 mb-4">Aucune vidéo pour le moment</p>
          <Button onClick={openAdd} className="bg-neon-lime text-black font-bold">
            <Upload className="w-4 h-4 mr-2" />
            Uploader ma première vidéo
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((v) => (
            <div
              key={v.id}
              data-testid={`video-card-${v.id}`}
              className="border border-zinc-800 bg-zinc-900/50 overflow-hidden group hover:border-neon-lime/50 transition-colors"
            >
              <div className="relative aspect-video bg-black">
                <video
                  src={`${API}/videos/${v.id}`}
                  className="w-full h-full object-cover"
                  preload="metadata"
                  muted
                  playsInline
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Play className="w-12 h-12 text-white" />
                </div>
                <div className="absolute top-2 left-2 flex gap-2">
                  {v.is_presentation && (
                    <span className="bg-neon-lime text-black text-xs font-bold px-2 py-1 flex items-center gap-1">
                      <Star className="w-3 h-3" /> Présentation
                    </span>
                  )}
                  {!v.is_visible && (
                    <span className="bg-zinc-700 text-white text-xs font-bold px-2 py-1">Masquée</span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-white truncate" title={v.title}>{v.title}</h3>
                {v.description && (
                  <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{v.description}</p>
                )}
                <div className="text-xs text-zinc-500 mt-2">
                  {formatSize(v.size)} • {new Date(v.created_at).toLocaleDateString("fr-FR")}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleVisibility(v)}
                    data-testid={`video-toggle-${v.id}`}
                    className="border-zinc-700 text-white hover:bg-zinc-800 flex-1 h-8 px-2"
                  >
                    {v.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(v)}
                    disabled={downloadingId === v.id}
                    data-testid={`video-download-${v.id}`}
                    className="border-neon-lime/50 text-neon-lime hover:bg-neon-lime/10 flex-1 h-8 px-2"
                    title="Télécharger"
                  >
                    {downloadingId === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(v)}
                    data-testid={`video-edit-${v.id}`}
                    className="border-zinc-700 text-white hover:bg-zinc-800 flex-1 h-8 px-2"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(v.id)}
                    data-testid={`video-delete-${v.id}`}
                    className="border-red-900 text-red-400 hover:bg-red-950 flex-1 h-8 px-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={(o) => { if (!uploading) { setIsDialogOpen(o); if (!o) setFormData(empty); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-unbounded">
              {editing ? "Modifier la vidéo" : "Nouvelle vidéo"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {!editing && (
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="video-dropzone"
                className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                  isDragging ? "border-neon-lime bg-neon-lime/5" : "border-zinc-700 hover:border-neon-lime/50"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
                {formData.file ? (
                  <div>
                    <VideoIcon className="w-12 h-12 mx-auto text-neon-lime mb-3" />
                    <p className="text-white font-bold truncate">{formData.file.name}</p>
                    <p className="text-zinc-400 text-sm mt-1">{formatSize(formData.file.size)}</p>
                    <p className="text-zinc-500 text-xs mt-2">Cliquez pour changer</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-12 h-12 mx-auto text-zinc-500 mb-3" />
                    <p className="text-white font-bold">Glissez votre vidéo ici</p>
                    <p className="text-zinc-400 text-sm mt-1">MP4, MOV ou WebM — aucune limite de taille</p>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-white">Titre *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Audi RS6 — Tour complet"
                data-testid="video-title"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-white">Description (optionnelle)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="video-description"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>

            <div className="space-y-3 border-t border-zinc-800 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white flex items-center gap-2">
                    <Star className="w-4 h-4 text-neon-lime" /> Vidéo de présentation du site
                  </Label>
                  <p className="text-zinc-400 text-xs mt-1">
                    Affichée sur la page d'accueil. Une seule à la fois.
                  </p>
                </div>
                <Switch
                  checked={formData.is_presentation}
                  onCheckedChange={(c) => setFormData({ ...formData, is_presentation: c })}
                  data-testid="video-presentation-toggle"
                />
              </div>
              {editing && (
                <div className="flex items-center justify-between">
                  <Label className="text-white">Visible sur le site</Label>
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(c) => setFormData({ ...formData, is_visible: c })}
                  />
                </div>
              )}
            </div>

            {uploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white">Upload en cours…</span>
                  <span className="text-neon-lime font-bold">{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-zinc-800 overflow-hidden">
                  <div className="h-full bg-neon-lime transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={uploading}
                className="border-zinc-700 text-white hover:bg-zinc-800 flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSave}
                disabled={uploading}
                data-testid="save-video-btn"
                className="bg-neon-lime text-black font-bold hover:bg-neon-lime/90 flex-1"
              >
                {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Upload…</>) : editing ? "Enregistrer" : "Uploader"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGalleryPage;
